import { useEffect } from "react";
import { useSchema } from "../contexts/schema.context";
import { locale, storeConfig } from "src/config/app-config";
import {
  toAbsoluteUrl,
  getBaseUrl,
  getPathByTypeAndGender,
  getAllBrandPath,
} from "../utils/getBaseUrl";
import {
  getBrandUrl,
  getGenderSegment,
} from "src/services/schema/utils/helpers";
import { localeFixOptional } from "src/utils/genderLocale/genderLocale";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import { buildCollectionPageSchema } from "src/services/schema/builders/collection-page.builder";
import { buildCorporationSchema } from "src/services/schema/builders/organization.builder";
import type { CorporationData } from "src/services/schema/builders/organization.builder";
import type { CollectionPageOptions } from "src/services/schema/utils/schema-generators";
import type { ProductListType, Gender } from "../types/product-listing";
import type { SchemaBlock } from "src/services/schema/types/schema.types";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { startCase } from "lodash-es";
import type { BrandData } from "src/types/adapters/browse.type";

interface PLPSchemaProps {
  productListType: ProductListType;
  title?: string;
  description?: string;
  gender?: Gender;
  slug: string;
  breadcrumbs?: { name: string; url?: string }[];
  /** Brand object — only for BRAND_PLP */
  brand?: BrandData;
}

/**
 * Generates the copyright year array from founding year to current year
 */
function getCopyrightYears(foundingYear = 2016): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = foundingYear; y <= currentYear; y++) {
    years.push(y);
  }
  return years;
}

/**
 * Hook that generates CollectionPage + Corporation schemas for PLPs.
 *
 * This complements `useItemListSchema` (which handles the product ItemList).
 * Together they produce the full @graph that marketing expects:
 *   @graph: [ItemList, CollectionPage, Corporation]
 *
 * Only adds CollectionPage for brand/category/collection PLPs (not search).
 * Always adds the Corporation entity so products can reference seller via @id.
 */
export const usePLPSchema = ({
  productListType,
  title,
  description,
  gender,
  slug,
  breadcrumbs,
  brand,
}: PLPSchemaProps): void => {
  const { addSchemas, removeSchemas } = useSchema();
  const { i18n } = useLingui();

  useEffect(() => {
    const schemaId = `plp-schema-${gender}-${slug}`;
    const schemas: SchemaBlock[] = [];
    const baseUrl = getBaseUrl(locale);

    // ── Corporation entity ──────────────────────────────────────────────────
    // Always added so products can reference it via { "@id": ".../#Otrium" }
    const corporationData: CorporationData = {
      name: "Otrium",
      url: `${baseUrl}/`,
      logo: `${baseUrl}/static/favicon.png`,
      description: description || "",
      foundingDate: "2016",
      idFragment: "Otrium",
      legalName: "Otrium",
      alternateName: `Otrium ${(
        storeConfig?.countryCode || "NL"
      ).toUpperCase()}`,
      image: `${baseUrl}/static/favicon.png`,
      email: "service@otrium.com",
      telephone: "+31202217184",
      sameAs: [
        "https://www.tiktok.com/@otrium",
        "https://www.instagram.com/otriumofficial/",
        "https://nl.linkedin.com/company/otrium",
        "https://www.facebook.com/otriumofficial/",
      ],
      address: {
        streetAddress: "Danzigerkade 15-C",
        postalCode: "1013AP",
        addressLocality: "Amsterdam",
        addressRegion: "NH",
        addressCountry: storeConfig?.countryCode || "NL",
      },
      returnPolicy: {
        idUrl: `https://help.otrium.${
          storeConfig?.countryCode === "NL" ? "nl" : "com"
        }/nl/articles/98-wat-is-het-retourbeleid-van-otrium#MerchantReturnPolicy`,
        applicableCountry: storeConfig?.countryCode || "NL",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
        returnMethod: ["https://schema.org/ReturnByMail"],
        merchantReturnLink: `https://help.otrium.${
          storeConfig?.countryCode === "NL" ? "nl" : "com"
        }/nl/articles/98-wat-is-het-retourbeleid-van-otrium`,
        merchantReturnDays: 60,
      },
    };

    schemas.push(buildCorporationSchema(corporationData));

    // ── CollectionPage entity ───────────────────────────────────────────────
    // Only for brand, category, and collections PLPs (not search or generic)
    const collectionPageTypes: ProductListType[] = [
      "BRAND_PLP",
      "CATEGORY_PLP",
      "COLLECTIONS_PLP",
      "SHOP_TYPE_PLP",
    ];

    if (collectionPageTypes.includes(productListType)) {
      const isBrand = productListType === "BRAND_PLP" && brand;
      const collectionName = isBrand ? brand.name : slug;
      const genderSegment = getGenderSegment(gender);

      // Build the canonical URL for this page
      const pagePath = getPathByTypeAndGender({
        type: isBrand ? "brand" : "category",
        gender: genderSegment,
        slug,
        locale,
      });
      const pageUrl = toAbsoluteUrl(pagePath) || "";
      const brandShopType = isBrand ? localeFixOptional({ gender }) : undefined;

      // Build breadcrumbs if not provided
      const collectionBreadcrumbs = breadcrumbs || [
        {
          name: "Home",
          url: toAbsoluteUrl(genderSegment ? `/${genderSegment}` : "/"),
        },
        ...(isBrand
          ? [
              {
                name: i18n._(msg`All brands`),
                url: toAbsoluteUrl(
                  genderSegment
                    ? `/${genderSegment}/${getAllBrandPath(locale)}`
                    : `/${getAllBrandPath(locale)}`
                ),
              },
            ]
          : []),
        ...(brandShopType
          ? [
              { name: collectionName, url: getBrandUrl(slug, locale) },
              {
                name: i18n._(shopTypeTranslation[brandShopType]),
                url: pageUrl,
              },
            ]
          : [{ name: collectionName, url: pageUrl }]),
      ];

      const collectionDescription =
        description ||
        i18n._(msg`Shop ${collectionName} ${gender} collection at Otrium.`);

      // Copyright metadata matching marketing's spec
      const aboutUrl = `https://about.otrium.com/${
        storeConfig?.countryCode?.toLowerCase() || "nl"
      }`;
      const copyrightOptions: CollectionPageOptions = {
        copyrightHolderId: `${aboutUrl}#mainEntityOfPage`,
        copyrightNotice: `© 2016-${new Date().getFullYear()} Otrium`,
        copyrightYear: getCopyrightYears(2016),
        publisherId: `${aboutUrl}#mainEntityOfPage`,
      };

      // Map locale to IETF BCP 47 format
      const inLanguage = locale
        ?.replace("-", "-")
        .replace(/(-.+)/, (m) => m.toUpperCase());

      const collectionPageSchema = buildCollectionPageSchema(
        {
          name:
            title ||
            `${startCase(collectionName?.replaceAll("-", " "))} - ${startCase(
              gender || ""
            )}`,
          description: collectionDescription,
          url: pageUrl,
          inLanguage,
          breadcrumbs: collectionBreadcrumbs,
        },
        copyrightOptions
      );

      schemas.push(collectionPageSchema);
    }

    addSchemas(schemas, schemaId);

    return () => removeSchemas(schemaId);
  }, [
    productListType,
    title,
    description,
    gender,
    slug,
    breadcrumbs,
    brand,
    locale,
    storeConfig,
    i18n,
    addSchemas,
    removeSchemas,
  ]);
};
